/**
 * `src/spa/routeGuard.ts` — enforce `allowedItems` on navigation, and pick the
 * landing route for `/`. Pure decision functions; wired to single-spa's
 * `before-routing-event` in `main.ts`.
 */
import type { TenantNavModule } from "@/nav/navFilter";
import { routeUnderPrefix } from "@/nav/navFilter";

interface ParsedAllowedRoute {
  pathname: string;
  /** `null` = the route carries no `?section=` at all (the module's default section). */
  section: string | null;
}

function parseAllowedRoute(route: string): ParsedAllowedRoute {
  const [pathname = "", query] = route.split("?");
  return { pathname, section: new URLSearchParams(query || "").get("section") };
}

/**
 * `null` = the navigation is allowed to proceed. A string = redirect there
 * instead (module-internal cross-links to a hidden section land on the
 * module's first allowed item — accepted per the design spec).
 *
 * Matches on pathname + the `section` query param ONLY (present values must
 * be equal; both absent counts as equal too) — every other query param is
 * ignored. A bare-path allowed item (no `section` at all, i.e. the module's
 * default) must NOT admit an arbitrary `?section=...` — comparing the raw
 * URL string let a stray/unlisted `?section=` slip through whenever the
 * bare pathname also happened to be an allowed entry.
 */
export function decideRouteGuard(pathname: string, search: string, modules: TenantNavModule[]): string | null {
  const mod = modules.find((m) => routeUnderPrefix(pathname, m.pathPrefix));
  if (!mod || mod.allowedItems == null || mod.allowedItems.length === 0) return null;

  const currentSection = new URLSearchParams(search).get("section");
  const isAllowed = mod.allowedItems.some((item) => {
    const parsed = parseAllowedRoute(item);
    return parsed.pathname === pathname && parsed.section === currentSection;
  });
  if (isAllowed) return null;
  return mod.allowedItems[0] ?? null;
}

/** Landing route for `/`: the tenant's configured `homePath`, else its first module's prefix. */
export function decideHomeRedirect(homePath: string | null, modules: TenantNavModule[]): string | null {
  if (homePath) return homePath;
  const first = modules[0];
  return first ? first.pathPrefix : null;
}
